<template>
  <!-- mobilde -->
  <div
    v-if="isMobile"
    class="reaction2-menu fixed top-0 left-0 z-full-page-loading w-full h-full flex invisible opacity-0"
    :class="{ active: isMenuOpen }"
  >
    <div class="bg-black-50 fixed w-full h-full top-0 left-0 right-0 bottom-0" @click.stop="closeMenu"></div>
    <div class="reaction2-menu--wrapper relative bg-white w-full mt-auto mx-auto pb-6 rounded-t-lg space-y-4">
      <div class="content-visibility-reactions">
        <header class="px-4 py-3 flex items-center justify-between">
          <span class="font-medium text-lg">İçeriği değerlendir</span>
          <div class="u-center" @click.stop="closeMenu">
            <OIcon icon="times" />
          </div>
        </header>
        <div class="border-t border-[#000]/[.1] w-full mb-4"></div>

        <div class="px-6 mb-4 reaction-list flex space-x-2">
          <div v-for="reaction in reactions" :id="reaction.id" :key="reaction.id" class="reaction-wrapper w-[42px] h-full">
            <div class="relative h-24">
              <div class="absolute inset-0 z-0 flex flex-col items-center justify-end">
                <div
                  :class="`w-full transition-all duration-200 rounded-full ${
                    reaction.newHeight || reaction.isActive ? 'bg-[#34D399]' : 'bg-[#E2E5ED]'
                  }`"
                  :style="{ height: `calc(${reaction.height}% + ${reaction.offset}px)` }"
                ></div>
              </div>
              <OButton
                pure
                :disabled="isReactionsDisabled"
                class="absolute bottom-0 z-0 bg-white rounded-full"
                :class="`w-full ${isReactionsDisabled ? 'cursor-wait' : ''}`"
                aria-label="tepki"
                @click.stop="handleReaction(reaction)"
              >
                <div class="h-[42px] w-[42px] bg-white rounded-full u-center border border-[#E2E5ED]}">
                  <div class="reaction u-center h-[28.45px] w-[28.45px]">
                    <div class="w-full h-full relative">
                      <div style="padding-bottom: 100%"></div>
                      <OImage
                        loading="lazy"
                        :src="reaction.icons.gif || reaction.icons.svg"
                        class="absolute inset-0 h-auto w-full"
                        alt=""
                      />
                    </div>
                  </div>
                </div>
              </OButton>
            </div>
            <div class="font-normal text-primary-300 text-13 text-center mt-2 leading-16">
              {{ reaction.count }}
            </div>
          </div>
        </div>
        <div v-if="showWarning" class="text-red-500 text-center text-sm -ml-3 mt-4">{{ $t('news-page.emoji-reaction-limit') }}</div>
      </div>
    </div>
  </div>
</template>
<script>
import { computed, defineComponent, onMounted, ref, useContext, inject } from '@nuxtjs/composition-api';
import { sendReactionCommentLayer } from '@/modules/tracking/scripts/dataLayer';
import { useDialog } from '~/composables/useModal';
import { useDevice } from '~/composables/useDevice';
import { useAuth } from '~/modules/auth/state/useAuth';
import { localStore } from '~/modules/common/utils/localStorageUtils';
import { OIcon, OButton, OImage } from '~/modules/common/components/ui';
import { eventBus } from '~/modules/common/utils/eventBus';

export default defineComponent({
  components: { OIcon, OButton, OImage },
  props: ['article'],
  setup(props) {
    const { $service } = useContext();
    const { openErrorDialog } = useDialog();
    const { isMobile } = useDevice();
    const { auth } = useAuth();

    const name = inject('dataLayerName')?.value;
    const articleId = props.article.legacyId;
    const userId = auth.value.isLoggedIn ? auth.value.user._id : 'anonym';
    const storageUserKey = `u${userId}`;
    const userReactions = ref({});
    const isReactionsDisabled = ref(false);
    const isMenuOpen = ref(false);
    const showWarning = ref(false);

    const reactions = computed(() => {
      const allReactions = props.article.reactions || [];
      // toplam reaction sayısı
      const totalCount = allReactions.reduce((total, reaction) => {
        total += reaction.count;
        return total;
      }, 0);

      allReactions.map((reaction) => {
        reaction.offset = 42;
        // her bir reaction sayısı ile toplam reactıon sayısına bölüp 100 ile çarparak, bir reaction'ın(emojinin) genel oyların yüzde kaçına sahip olduğunu hesaplıyoruz
        reaction.height = totalCount ? (reaction.count / totalCount) * 100 : 0;
        if (reaction.height < 1) reaction.height = 1; // eğer %1'den küçükse 1% height veriyoruz height'ın çok ince olmaması için

        // reaction daha önce verilmiş mi?
        if (process.client) reaction.isActive = getCurrentUserReactions().includes(reaction.id);

        return reaction;
      });

      return allReactions;
    });

    const unlockScroll = () => document.getElementsByTagName('body')[0].classList.remove('no-scroll');
    const lockScroll = () => document.getElementsByTagName('body')[0].classList.add('no-scroll');

    const closeMenu = () => {
      unlockScroll();
      eventBus.emit('isArticleReactionsModalClosed');
      isMenuOpen.value = false;
    };

    const getCurrentUserReactions = () => {
      const result = localStore.getItem('o-rcts')?.[storageUserKey]?.[articleId];
      return result || [];
    };

    const addReactionStorage = (id) => {
      const newReactions = [...getCurrentUserReactions(), id];
      userReactions.value[storageUserKey][articleId] = newReactions;
      localStore.setItem('o-rcts', userReactions.value);
    };

    const removeReactionStorage = (id) => {
      const newReactions = getCurrentUserReactions().filter((itemId) => itemId !== id);
      userReactions.value[storageUserKey][articleId] = newReactions;
      localStore.setItem('o-rcts', userReactions.value);
    };

    const handleReaction = async (reaction) => {
      isReactionsDisabled.value = true;
      try {
        if (getCurrentUserReactions().includes(reaction.id)) {
          if (getCurrentUserReactions().length <= 3) {
            showWarning.value = false;
          }
          await $service.article.removeReaction(props.article._id, reaction.id);
          reaction.count -= 1;
          reaction.newHeight = null; // bar'ı eski boyutuna getir
          removeReactionStorage(reaction.id);
        } else {
          if (getCurrentUserReactions().length === 3) {
            showWarning.value = true;
            return;
          }
          await $service.article.addReaction(props.article._id, reaction.id);
          reaction.count += 1;
          reaction.newHeight = reaction.height + 6; // bar'ı büyüt
          addReactionStorage(reaction.id);
          sendReactionCommentLayer('reaction', { article: props.article }, reaction, name, 'Keşfet');
        }
      } catch (error) {
        openErrorDialog({ error });
      } finally {
        isReactionsDisabled.value = false;
      }
    };

    onMounted(() => {
      userReactions.value = localStore.getItem('o-rcts');
      if (!userReactions.value) {
        userReactions.value = {};
        localStore.setItem('o-rcts', userReactions.value);
      }
      if (!userReactions.value[storageUserKey]) {
        userReactions.value[storageUserKey] = {};
        localStore.setItem('o-rcts', userReactions.value);
      }
      setTimeout(() => {
        isMenuOpen.value = true;
        lockScroll();
      }, 10);
    });

    return {
      reactions,
      handleReaction,
      showWarning,
      isReactionsDisabled,
      closeMenu,
      isMobile,
      isMenuOpen,
    };
  },
});
</script>

<style scoped lang="scss">
.content-visibility-reactions {
  contain-intrinsic-size: 100px 120px;
  content-visibility: auto;
  @include sm() {
    content-visibility: visible;
  }
}
.reaction2-menu {
  transition: all 0.3s ease;

  &.active {
    opacity: 1;
    visibility: visible;

    .reaction2-menu--wrapper {
      transform: translateY(0);
    }
  }

  &--wrapper {
    transform: translateY(100%);
    will-change: transform;
    transition: all 0.6s ease;
    transition-delay: 0.2s;
  }
}

@media screen and (max-width: 428px) {
  .invisible {
    margin-left: 0 !important;
  }
}
</style>
